// Event helpers: formatters, categories, and iCal export.
// The catalog itself lives in the database — see src/lib/events.functions.ts.

export type EventCategoryId =
  | "nervous"
  | "trauma"
  | "community"
  | "sleep"
  | "dreams"
  | "journaling"
  | "boundaries"
  | "selfworth"
  | "innerchild"
  | "guest";

export type EventCategory = {
  id: EventCategoryId;
  label: string;
  emoji: string;
  hue: string;
};

export const EVENT_CATEGORIES: EventCategory[] = [
  { id: "nervous", label: "Nervous System Support", emoji: "🌿", hue: "from-emerald-100 to-teal-50 text-emerald-900" },
  { id: "trauma", label: "Trauma Recovery", emoji: "🤍", hue: "from-rose-100 to-amber-50 text-rose-900" },
  { id: "community", label: "Community Circle", emoji: "💬", hue: "from-violet-100 to-indigo-50 text-violet-900" },
  { id: "sleep", label: "Sleep & Dreams", emoji: "🛌", hue: "from-indigo-100 to-blue-50 text-indigo-900" },
  { id: "dreams", label: "Dream Exploration", emoji: "🌙", hue: "from-purple-100 to-fuchsia-50 text-purple-900" },
  { id: "journaling", label: "Journaling Workshops", emoji: "📖", hue: "from-amber-100 to-orange-50 text-amber-900" },
  { id: "boundaries", label: "Boundaries", emoji: "🛡", hue: "from-sky-100 to-cyan-50 text-sky-900" },
  { id: "selfworth", label: "Self-Worth", emoji: "💛", hue: "from-yellow-100 to-amber-50 text-yellow-900" },
  { id: "innerchild", label: "Inner Child Healing", emoji: "🌱", hue: "from-lime-100 to-emerald-50 text-lime-900" },
  { id: "guest", label: "Special Guest Sessions", emoji: "✨", hue: "from-pink-100 to-rose-50 text-pink-900" },
];

export function getCategory(id: string): EventCategory {
  return EVENT_CATEGORIES.find((c) => c.id === id) ?? EVENT_CATEGORIES[2];
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** The one time zone every Recovery Trauma™ event is scheduled in. */
export const UK_TIME_ZONE = "Europe/London";

function parts(iso: string, timeZone: string) {
  const d = new Date(iso);
  const f = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(d)) out[p.type] = p.value;
  return out;
}

/** "Sun 16 Aug" in UK time. */
export function formatEventDate(iso: string): string {
  try {
    const p = parts(iso, UK_TIME_ZONE);
    return `${p['weekday']} ${p['day']} ${p['month']}`;
  } catch {
    const d = new Date(iso);
    return `${DAY_NAMES[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]}`;
  }
}

/** "9:00 PM UK Time". */
export function formatEventTime(iso: string): string {
  try {
    const p = parts(iso, UK_TIME_ZONE);
    return `${p['hour']}:${p['minute']} ${(p['dayPeriod'] ?? "").toUpperCase()} UK Time`;
  } catch {
    return "9:00 PM UK Time";
  }
}

// --- Local / UK dual-time formatters -------------------------------------
// Displays e.g. "Sunday, 16 August 2026 · 5:00 PM" in whichever time zone
// the browser is in, and "Sunday, 16 August 2026 · 10:00 PM BST" for the
// UK host reference. SSR safe: falls back to UTC when no tz is available.

export function formatInZone(iso: string, timeZone?: string, withTzAbbr = false): string {
  const d = new Date(iso);
  try {
    const datePart = new Intl.DateTimeFormat("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone,
    }).format(d);
    const timePart = new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone,
      timeZoneName: withTzAbbr ? "short" : undefined,
    })
      .format(d)
      // en-GB emits "5:00 pm" — uppercase for calmer display.
      .replace(/\bam\b/, "AM")
      .replace(/\bpm\b/, "PM");
    return `${datePart} · ${timePart}`;
  } catch {
    return `${d.toUTCString()}`;
  }
}

/** The visitor's browser time zone, or UK time when it can't be detected. */
export function detectTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || UK_TIME_ZONE;
  } catch {
    return UK_TIME_ZONE;
  }
}

/** Offset (ms) of `timeZone` from UTC at the given instant. */
function zoneOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return asUtc - date.getTime();
}

/**
 * Convert a wall-clock value from a `datetime-local` input ("2026-09-01T21:00")
 * that the admin entered *in `timeZone`* into a UTC ISO string. DST-aware.
 */
export function zonedInputToIso(local: string, timeZone: string): string {
  const [datePart, timePart = "00:00"] = local.split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  const naive = Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
  let ts = naive - zoneOffsetMs(new Date(naive), timeZone);
  // Re-check once: the offset may differ across a DST boundary.
  ts = naive - zoneOffsetMs(new Date(ts), timeZone);
  return new Date(ts).toISOString();
}

/** Inverse of `zonedInputToIso` — ISO to a `datetime-local` value in `timeZone`. */
export function isoToZonedInput(iso: string, timeZone: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${String(Number(get("hour")) % 24).padStart(2, "0")}:${get("minute")}`;
}

/** User's local time — no abbreviation ("Sunday, 16 August 2026 · 5:00 PM"). */
export function formatEventLocal(iso: string): string {
  const tz =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined;
  return formatInZone(iso, tz, false);
}

/** UK host time with BST/GMT suffix ("Sunday, 16 August 2026 · 10:00 PM BST"). */
export function formatEventUK(iso: string): string {
  return formatInZone(iso, UK_TIME_ZONE, true);
}

/** Short dual-time line for emails and confirmations. */
export function formatEventDualTime(iso: string, timeZone?: string): string {
  const uk = formatEventUK(iso);
  if (!timeZone || timeZone === UK_TIME_ZONE) return uk;
  return `${uk} (your local time: ${formatInZone(iso, timeZone, true)})`;
}



// --- iCal (.ics) ---------------------------------------------------------

export type IcsEvent = {
  slug: string;
  title: string;
  description: string;
  starts_at: string;
  duration_minutes: number;
  zoom_url?: string | null;
};

function toIcsDate(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

export function buildIcs(event: IcsEvent): string {
  const start = toIcsDate(event.starts_at);
  const end = toIcsDate(
    new Date(new Date(event.starts_at).getTime() + event.duration_minutes * 60_000).toISOString(),
  );
  const uid = `${event.slug}@recoverytrauma`;
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Recovery Trauma™//Live Events//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${toIcsDate(new Date().toISOString())}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${event.description.replace(/\n/g, "\\n")}${event.zoom_url ? `\\n\\nJoin: ${event.zoom_url}` : ""}`,
    ...(event.zoom_url ? [`URL:${event.zoom_url}`] : []),
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function downloadIcs(event: IcsEvent) {
  if (typeof window === "undefined") return;
  const blob = new Blob([buildIcs(event)], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${event.slug}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- Discussion threads (localStorage, frontend-only for now) ------------

export type DiscussionMsg = {
  id: string;
  name: string;
  body: string;
  at: number;
};

const DISCUSS_KEY = (slug: string) => `rt:event-discussion:${slug}`;

export function getDiscussion(slug: string): DiscussionMsg[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(DISCUSS_KEY(slug)) || "[]");
  } catch {
    return [];
  }
}

export function postDiscussion(slug: string, msg: Omit<DiscussionMsg, "id" | "at">) {
  const list = getDiscussion(slug);
  const next: DiscussionMsg = {
    ...msg,
    id: Math.random().toString(36).slice(2),
    at: Date.now(),
  };
  list.push(next);
  localStorage.setItem(DISCUSS_KEY(slug), JSON.stringify(list));
  return next;
}
